import React, { useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, MessageSquarePlus, Bug, Lightbulb, Send } from "lucide-react";

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("feature");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Add a quick title");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        type, title, description,
        page: window.location.pathname,
      });
      toast.success(type === "bug" ? "Bug report submitted — thanks!" : "Feature request submitted — thanks!");
      setOpen(false);
      setTitle("");
      setDescription("");
      setType("feature");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="feedback-btn"
        onClick={() => setOpen(true)}
        title="Submit feedback or bug report"
        className="w-8 h-8 surface-card hover:border-primary-accent flex items-center justify-center"
      >
        <MessageSquarePlus size={14} />
      </button>

      {open && createPortal((
        <div
          data-testid="feedback-modal"
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 safe-top safe-bottom"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div className="w-full max-w-lg surface-card max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <div className="label-tag">Help us improve</div>
                <h2 className="font-display text-xl uppercase">Send Feedback</h2>
              </div>
              <button
                data-testid="feedback-close"
                onClick={() => setOpen(false)}
                className="w-8 h-8 hover:text-primary-accent flex items-center justify-center"
              ><X size={18}/></button>
            </div>

            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="label-tag block mb-2">Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "feature", l: "Feature", icon: Lightbulb },
                    { v: "bug",     l: "Bug",     icon: Bug },
                    { v: "other",   l: "Other",   icon: MessageSquarePlus },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const selected = type === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        data-testid={`feedback-type-${opt.v}`}
                        onClick={() => setType(opt.v)}
                        className={`flex items-center justify-center gap-2 py-2 border text-xs uppercase tracking-widest ${
                          selected ? "bg-primary-accent border-primary-accent text-white" : "border-zinc-800 text-zinc-400 hover:border-white/30"
                        }`}
                      ><Icon size={12}/> {opt.l}</button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="label-tag block mb-2">Title <span className="text-primary-accent">*</span></label>
                <input
                  data-testid="feedback-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required maxLength={200}
                  placeholder={type === "bug" ? "Eg: Survivor pick doesn't save on mobile" : "Eg: Add SMS reminders before kickoff"}
                  className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="label-tag block mb-2">Details</label>
                <textarea
                  data-testid="feedback-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5} maxLength={5000}
                  placeholder={type === "bug" ? "Steps to reproduce, what you expected, what actually happened…" : "Why is this useful? Who else benefits?"}
                  className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none resize-none"
                />
                <div className="text-xs text-zinc-600 mt-1 text-right">{description.length}/5000</div>
              </div>

              <div className="text-xs text-zinc-500">Submitted from: <span className="text-zinc-300">{window.location.pathname}</span></div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                <button type="button" onClick={() => setOpen(false)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">Cancel</button>
                <button
                  data-testid="feedback-submit"
                  type="submit"
                  disabled={busy}
                  className="bg-primary-accent text-white px-5 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                ><Send size={12}/> {busy ? "Submitting…" : "Submit"}</button>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
